// Files Data Base — the thumbnails a device has already downloaded, kept on that device.
// thumbs.js fetches the ORIGINAL of every photo and shrinks it in the browser; without this file
// that download repeats on every visit. Here the shrunk result is kept in the browser's Cache API
// (no service worker needed), so a thumbnail is downloaded once per device.
//
// Why a path-keyed cache cannot go stale: a Files storage key derives from the file's sha256
// (parse/fileKeys.js), so one path is always the same bytes — a changed photo is a new path.
//
// Rules: one cache per signed-in user (never shared across accounts on a shared PC); only small
// results are kept (MAX_BYTES); the oldest entries are trimmed (MAX_ENTRIES); every call is
// best-effort. Sign-out cleanup is best-effort too: the page reloads right after sign-out and can
// beat the delete, so whoever opens Files next also removes every cache that is not theirs
// (purgeOthers) — the guarantee is "never SHOWN to another user", not "never on disk".
// A read that does not answer within READ_MS counts as a miss, so a stuck Cache API cannot
// occupy the six download slots — a missing or failing Cache API (private window, quota, old browser) means "not
// cached", never an error. All writes are fire-and-forget.
const PREFIX = 'clb-files-thumbs-v1:';
const HOST = 'https://clb-thumbs.local';
export const MAX_ENTRIES = 5000;
export const MAX_BYTES = 512 * 1024;
const TRIM_EVERY = 50;
const READ_MS = 1500;

let writes = 0;
const store = () => (typeof caches !== 'undefined' ? caches : undefined);   // the browser's CacheStorage
const keyOf = (path, size) => `${HOST}/${size.w}x${size.h}/${encodeURIComponent(path)}`;
const open = (owner) => store().open(`${PREFIX}${owner}`);

/** The kept thumbnail Blob for (owner, path, size), or null. */
export function readThumb(owner, path, size) {
  const look = (async () => {
    try {
      if (!store() || !owner) return null;
      const hit = await (await open(owner)).match(keyOf(path, size));
      return hit ? await hit.blob() : null;
    } catch (_) { return null; }
  })();
  return Promise.race([look, new Promise((resolve) => setTimeout(() => resolve(null), READ_MS))]);
}

/** Keep at most MAX_ENTRIES for this user (oldest written first). */
export async function trimThumbs(owner) {
  try {
    if (!store() || !owner) return;
    await trim(await open(owner));
  } catch (_) { /* best-effort */ }
}

async function trim(cache) {
  const keys = await cache.keys();
  for (let i = 0; i < keys.length - MAX_ENTRIES; i += 1) await cache.delete(keys[i]);   // keys() is oldest first
}

/** Keep a thumbnail. Never throws, never awaited by the caller's render path. */
export function writeThumb(owner, path, size, blob) {
  (async () => {
    try {
      if (!store() || !owner || !blob || blob.size > MAX_BYTES) return;
      const cache = await open(owner);
      await cache.put(keyOf(path, size), new Response(blob, { headers: { 'Content-Type': blob.type || 'image/jpeg' } }));
      writes += 1;
      if (writes === 1 || writes % TRIM_EVERY === 0) await trim(cache);   // the first write of a page load too: the counter restarts on every load
    } catch (_) { /* quota or unsupported — simply not cached */ }
  })();
}

/** Forget every kept thumbnail of every user — on sign-out. */
export async function clearThumbs() {
  try {
    if (!store()) return;
    const names = await store().keys();
    await Promise.all(names.filter((n) => n.startsWith(PREFIX)).map((n) => store().delete(n)));
  } catch (_) { /* nothing to clear */ }
}

/** Remove every kept-thumbnail cache that belongs to someone other than `owner`. */
export async function purgeOthers(owner) {
  try {
    if (!store() || !owner) return;
    const mine = `${PREFIX}${owner}`;
    const names = await store().keys();
    await Promise.all(names.filter((n) => n.startsWith(PREFIX) && n !== mine).map((n) => store().delete(n)));
  } catch (_) { /* nothing to purge */ }
}
